import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  AuthResult,
  FeatureName,
  GetUserByEmailPayload,
  TaxDetails,
  UpdateTaxDetailsPayload,
  User,
  UserCompanyFeature,
  LoginPayload,
  RegisterPayload,
  UploadLogoPayload,
  PatchUserPrefixesPayload,
} from './auth.types'
import { getAccessToken } from '../../utils/common'

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`,
    timeout: 2 * 60 * 1000, // 2 minutes
  }),
  endpoints: (builder) => ({
    register: builder.mutation<AuthResult, RegisterPayload>({
      query: (body) => ({
        url: 'oapi/v1/auth/register',
        method: 'POST',
        body,
      }),
    }),

    login: builder.mutation<AuthResult, LoginPayload>({
      query: (body) => ({
        url: 'oapi/v1/auth/login',
        method: 'POST',
        body,
      }),
    }),

    getUserByEmail: builder.query<User, GetUserByEmailPayload>({
      query: ({ email }) => ({
        url: `oapi/v1/users/email/${email}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),
    }),

    getUserById: builder.query<User, { userId: string }>({
      query: ({ userId }) => ({
        url: `oapi/v1/users/${userId}`,
        method: 'GET',
        headers: {
          'onboard-API-KEY': 'onboardingsecret',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),
    }),

    getUserFeatures: builder.query<UserCompanyFeature[], { userId: string }>({
      query: ({ userId }) => ({
        url: `oapi/v1/users/${userId}/features`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),
    }),

    updateUserFeature: builder.mutation<
      UserCompanyFeature,
      {
        userId: string
        companyId: string
        featureName: FeatureName
        enabled: boolean
      }
    >({
      query: ({ userId, companyId, featureName, enabled }) => ({
        url: `oapi/v1/users/${userId}/features/${companyId}/${featureName}`,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: { enabled },
      }),
    }),

    updateUserById: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: `oapi/v1/users/${body?.id}`,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: {
          ...body,
          drivers: [],
        },
      }),
    }),

    patchUserById: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: `oapi/v1/users/${body?.id}`,
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: {
          ...body,
        },
      }),
    }),

    patchUserPrefixes: builder.mutation<User, PatchUserPrefixesPayload>({
      query: ({ userId, ...body }) => ({
        url: `oapi/v1/users/prefix/${userId}`,
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body,
      }),
    }),

    updateTaxDetails: builder.mutation<TaxDetails, UpdateTaxDetailsPayload>({
      query: ({ userId, taxName, taxNumber, taxPercentage }) => ({
        url: `oapi/v1/users/${userId}/tax`,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: { taxName, taxNumber, taxPercentage },
      }),
    }),

    uploadLogo: builder.mutation<any, UploadLogoPayload>({
      query: ({ userId, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `oapi/v1/users/${userId}/upload`,
          body: formData,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      },
    }),

    forgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: ({ email }) => ({
        url: `oapi/v1/auth/forgot-password/${email}`,
        method: 'POST',
      }),
    }),

    resetPassword: builder.mutation<
      { message: string },
      { userId: string; code: string; password: string }
    >({
      query: ({ userId, code, password }) => ({
        url: `oapi/v1/auth/reset-password?userId=${userId}&code=${code}`,
        method: 'POST',
        body: { password },
      }),
    }),
  }),
})

export const {
  useRegisterMutation,
  useLoginMutation,
  useGetUserByEmailQuery,
  useLazyGetUserByEmailQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useLazyGetUserFeaturesQuery,
  useGetUserFeaturesQuery,
  useUpdateUserFeatureMutation,
  useUpdateUserByIdMutation,
  useUploadLogoMutation,
  usePatchUserByIdMutation,
  usePatchUserPrefixesMutation,
  useUpdateTaxDetailsMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApi
